import { camelCase } from 'lodash'
import OpenAPIClientAxios from 'openapi-client-axios'

import { V3RequestInterceptor } from '@webapp/config/api/v3'
import { IntegrationServicePlatforms } from '@webapp/models/enums/IntegrationPlatforms'
import type {
  Client as IntegrationsClientSchema,
  Components,
  Paths,
} from '../../core/services/IntegrationsAPIClient/client.d'
import definition from '../../core/services/IntegrationsAPIClient/openapi.json'
const BASE_URL = '/api/v3/integrations/proxy' as const

export const integrationsClient = new OpenAPIClientAxios({
  definition,
  axiosConfigDefaults: {
    withCredentials: true,
    baseURL: BASE_URL,
    xsrfCookieName: 'csrftoken',
    xsrfHeaderName: 'X-CSRFToken',
  },
  transformOperationName: (operation) => camelCase(operation),
}).initSync<IntegrationsClientSchema>()

integrationsClient.api.client.interceptors.request.use(V3RequestInterceptor)
integrationsClient.api.client.interceptors.request.use((request) => {
  request.url = request.url.endsWith('/') ? request.url : `${request.url}/`
  return request
})

export const integrationsQueryKey = 'integrations'

export { BASE_URL as BASE_URL_INTEGRATIONS }
export type { Client as IntegrationsClientSchema } from '../../core/services/IntegrationsAPIClient/client.d'

export type { Components, Paths }

interface IntegrationCodatQbo
  extends Omit<Components.Schemas.IntegrationResponse, 'config'> {
  config: Components.Schemas.CodatQuickBooksOnlineConfig
}

interface IntegrationCodatQboSandbox
  extends Omit<Components.Schemas.IntegrationResponse, 'config'> {
  config: Components.Schemas.CodatQuickBooksOnlineSandboxConfig
}

interface IntegrationCodatSageIntacct
  extends Omit<Components.Schemas.IntegrationResponse, 'config'> {
  config: Components.Schemas.CodatSageIntacctConfig
}

interface IntegrationCodatDynamics365BusinessCentral
  extends Omit<Components.Schemas.IntegrationResponse, 'config'> {
  config: Components.Schemas.CodatDynamics365BusinessCentralConfig
}

interface IntegrationMergeQbo
  extends Omit<Components.Schemas.IntegrationResponse, 'config'> {
  config: Components.Schemas.MergeConfig
}

export const isIntegrationCodatQbo = (
  integration: Components.Schemas.IntegrationResponse
): integration is IntegrationCodatQbo => {
  return integration.name === IntegrationServicePlatforms.QUICKBOOKS_ONLINE
}

export const isIntegrationCodatQboSandbox = (
  integration: Components.Schemas.IntegrationResponse
): integration is IntegrationCodatQboSandbox => {
  return (
    integration.name === IntegrationServicePlatforms.QUICKBOOKS_ONLINE_SANDBOX
  )
}

export const isIntegrationCodatSageIntacct = (
  integration: Components.Schemas.IntegrationResponse
): integration is IntegrationCodatSageIntacct => {
  return integration.name === IntegrationServicePlatforms.SAGE_INTACCT
}

export const isIntegrationCodatDynamics365BusinessCentral = (
  integration: Components.Schemas.IntegrationResponse
): integration is IntegrationCodatDynamics365BusinessCentral => {
  return (
    integration.name ===
    IntegrationServicePlatforms.DYNAMICS_365_BUSINESS_CENTRAL
  )
}

export const isIntegrationMergeQbo = (
  integration: Components.Schemas.IntegrationResponse
): integration is IntegrationMergeQbo => {
  return (
    integration.name === IntegrationServicePlatforms.MERGE_QUICKBOOKS_ONLINE
  )
}

// Integration client does not emit types for GET integration connection endpoint
export interface IntegrationMergeConnection {
  link_token: string
}

// Integration Settings type guards
export const INTEGRATION_SETTINGS_KEYS = {
  USE_BILL_TAX: 'use_bill_tax',
} as const

export interface IntegrationSettingBooleanTypeSetting
  extends Components.Schemas.IntegrationSettingResponse {
  setting_key: typeof INTEGRATION_SETTINGS_KEYS.USE_BILL_TAX
  setting_value: boolean
}

export const isIntegrationSettingBooleanTypeSetting = (
  setting: Components.Schemas.IntegrationSettingResponse
): setting is IntegrationSettingBooleanTypeSetting => {
  return setting.setting_key === INTEGRATION_SETTINGS_KEYS.USE_BILL_TAX
}
